import {
  E,
  clamp,
  numeric,
  short,
  text,
  line,
  rect,
  circle,
  path,
  point,
  arc,
  sample,
  val,
  angle,
  numberValue,
  reading,
  referenceLabel,
  missing,
  tone,
  simple,
  svgId,
  part,
} from "./render-kit.js";
import {
  convert,
  dimensionForUnit,
  durationText,
  smoothSeries,
} from "./units.js";

export const instrumentRendererIds = Object.freeze([
  "compass",
  "head-up",
  "wind-rose",
  "close-wind",
  "wind-angle",
  "rudder",
  "turn-rate",
  "heel",
  "attitude",
  "set-drift",
  "number",
  "trend-number",
  "state",
  "text-value",
  "multi-text",
  "status-strip",
  "counter",
  "timer",
  "analog-clock",
  "digital-clock",
  "arc-gauge",
  "needle-gauge",
  "vacuum",
  "vertical-meter",
  "horizontal-meter",
  "borderless-bar",
  "tank",
]);

const A = "var(--accent)",
  T = "var(--text)",
  M = "var(--muted)",
  G = "var(--grid)";
const B = "var(--background)",
  S = "var(--surface)";
const motion = 'data-motion="instrument"';
const bold = 'font-weight="650" letter-spacing="-1.8" data-motion="value"';
const normalized = (v) => ((v % 360) + 360) % 360;
const signedAngle = (v) => ((normalized(v) + 180) % 360) - 180;
const signedLabel = (v) =>
  `${Math.abs(signedAngle(v)).toFixed(0)}° ${v < 0 ? "P" : v > 0 ? "S" : ""}`.trim();

function defs(o) {
  const id = svgId(o, "instrument");
  return `<defs><radialGradient id="${id}-face"><stop stop-color="var(--surface)" stop-opacity=".9"/><stop offset="1" stop-color="var(--background)"/></radialGradient><linearGradient id="${id}-fill" x1="0" y1="0" x2="0" y2="1"><stop stop-color="var(--accent)" stop-opacity=".72"/><stop offset="1" stop-color="var(--accent)" stop-opacity=".12"/></linearGradient><linearGradient id="${id}-track" x1="0" x2="1"><stop stop-color="var(--accent)" stop-opacity=".18"/><stop offset="1" stop-color="var(--accent)" stop-opacity=".85"/></linearGradient></defs>`;
}

function ribbon(
  cx,
  cy,
  inner,
  outer,
  bearing,
  spread,
  color = A,
  opacity = 0.13,
  name = "ribbon",
) {
  const a = point(cx, cy, outer, bearing - spread),
    b = point(cx, cy, outer, bearing + spread);
  const c = point(cx, cy, inner, bearing + spread),
    d = point(cx, cy, inner, bearing - spread);
  return `<path d="M${a.x} ${a.y} A${outer} ${outer} 0 0 1 ${b.x} ${b.y} L${c.x} ${c.y} A${inner} ${inner} 0 0 0 ${d.x} ${d.y} Z" fill="${color}" opacity="${opacity}" ${part(name, motion)}/>`;
}

function marker(
  cx,
  cy,
  radius,
  bearing,
  style = "needle",
  color = A,
  size = 1,
  name = "needle",
) {
  const named = part(name);
  const shape =
    style === "tick"
      ? `<rect x="-4" y="-7" width="8" height="14" rx="4" fill="${color}" ${named}/>`
      : style === "chevron"
        ? `<path d="M-8 -6 L0 6 L8 -6" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" ${named}/>`
        : `<path d="M0 10 L-8 -8 Q0 -3 8 -8 Z" fill="${color}" ${named}/><path d="M-3 -4 L0 4 L3 -4" fill="none" stroke="${B}" stroke-opacity=".45" stroke-width="1" ${named}/>`;
  return `<g transform="translate(${cx} ${cy}) rotate(${bearing}) translate(0 ${-radius}) scale(${size})" ${motion}>${shape}</g>`;
}

function dial(
  o,
  {
    cx = 200,
    cy = 153,
    r = 76,
    rotation = 0,
    cardinals = true,
    labels = true,
    step,
    fill = true,
  } = {},
) {
  const quiet = simple(o),
    id = svgId(o, "instrument");
  let out = circle(
    cx,
    cy,
    r,
    fill && !quiet ? `url(#${id}-face)` : "none",
    G,
    quiet ? 1.3 : 1,
    part("dial"),
  );
  if (!quiet) out += circle(cx, cy, r - 13, "none", G, 0.7, part("dial"));
  const increment = step ?? (quiet ? 30 : o.ticks === "fine" ? 3 : 5);
  for (let v = 0; v < 360; v += increment) {
    const p = point(cx, cy, r + 8, v + rotation),
      q = point(cx, cy, r + (v % 30 === 0 ? 2 : 5), v + rotation);
    out += line(
      p.x,
      p.y,
      q.x,
      q.y,
      v % 90 === 0 ? M : G,
      v % 30 === 0 ? 1.5 : 1,
      part("ticks"),
    );
  }
  if (labels)
    for (let v = 0; v < 360; v += quiet ? 90 : 30) {
      if (quiet && !cardinals) continue;
      const p = point(cx, cy, r + 19, v + rotation);
      const cardinal = v % 90 === 0 && cardinals;
      const label = cardinal
        ? ["N", "E", "S", "W"][v / 90]
        : String(v).padStart(3, "0");
      out += text(
        p.x,
        p.y + 3.5,
        label,
        v % 90 === 0 ? 11 : 8,
        v === 0 && cardinals ? A : M,
        "middle",
        part(
          cardinal ? "cardinals" : "tick-labels",
          v % 90 === 0 ? 'font-weight="600"' : "",
        ),
      );
    }
  return out;
}

function pill(x, y, width, label, color = M) {
  return (
    rect(x, y, width, 22, S, 11, `stroke="${G}"`) +
    text(x + width / 2, y + 15, label, 10, color, "middle")
  );
}

function angularZones(cx, cy, r, zones = [], rotation = 0) {
  return zones
    .map((z) =>
      numeric(z.min) && numeric(z.max)
        ? path(
            arc(cx, cy, r, z.min + rotation, z.max + rotation),
            tone(z.tone),
            4,
            part("zones", 'opacity=".65"'),
          )
        : "",
    )
    .join("");
}

function centerReading(
  data,
  role,
  o,
  cx = 200,
  cy = 153,
  label = "",
  size = 38,
  name = "value",
) {
  const f = reading(data, role, o);
  return (
    (label
      ? text(
          cx,
          cy - 24,
          label,
          8,
          M,
          "middle",
          part("caption", 'letter-spacing="1.8"'),
        )
      : "") +
    text(
      cx,
      cy + 10,
      f.value,
      Math.min(size, 142 / Math.max(1, String(f.value).length * 0.56)),
      f.quality === "stale" || f.value === "—" ? M : T,
      "middle",
      part(name, bold),
    ) +
    text(cx, cy + 29, f.unit, 10, M, "middle", part("unit"))
  );
}

function compassFace(id, data, o) {
  const heading = angle(data, "heading");
  if (heading === null) return missing("Heading source unavailable");
  const up = id === "head-up" || o.orientation === "heading-up";
  let out =
    defs(o) +
    dial(o, {
      rotation: up ? -heading : 0,
      cardinals: o.cardinalLabels !== false,
    });
  if (!simple(o)) out += circle(200, 153, 51, B, G, 0.6, part("face"));
  const pointers =
    id === "head-up"
      ? o.pointers
      : [{ role: "heading", style: o.pointer, badge: false }];
  pointers.forEach((p, k) => {
    const a = angle(data, p.role);
    if (a === null) return;
    const bearing = a - (up ? heading : 0);
    out +=
      ribbon(200, 153, 52, 76, bearing, simple(o) ? 5 : 12, k ? M : A, 0.09) +
      marker(
        200,
        153,
        k ? 63 : 79,
        bearing,
        p.style,
        k ? T : A,
        1,
        k ? "pointer" : "needle",
      );
    if (p.badge)
      out += text(
        k % 2 ? 376 : 24,
        237 - Math.floor(k / 2) * 15,
        `${short(p.role, 9)} ${Math.round(normalized(a)) % 360}°`,
        9,
        k ? M : A,
        k % 2 ? "end" : "start",
        part("secondary"),
      );
  });
  out += angularZones(200, 153, 72, o.sectorArcs ?? o.zones, up ? -heading : 0);
  if (o.center !== "none")
    out += centerReading(
      data,
      id === "head-up" ? o.centerRole : "heading",
      o,
      200,
      153,
      (id === "head-up" ? o.centerRole : "heading").toUpperCase(),
      35,
    );
  out += text(
    200,
    254,
    short(
      referenceLabel(data, ["heading", "course"]) ||
        "Heading reference unavailable",
      42,
    ),
    9,
    M,
    "middle",
    part("caption"),
  );
  return out;
}

function windRose(data, o) {
  // With no apparent angle the rose stays, its pointers and badges left out
  // and its centre a muted "—" (Mike, 2026-10-14, TOM-665).
  const heading = angle(data, "heading"),
    absolute = o.frame === "absolute" || o.orientation === "north-up";
  if (absolute && heading === null)
    return missing("Compatible heading source required");
  const kind =
    o.windDesign && o.windDesign !== "auto"
      ? o.windDesign
      : simple(o)
        ? "clear"
        : "halo";
  const side = kind === "sail" || kind === "tactical",
    cx = side ? 140 : 200,
    cy = 150,
    r = side ? 72 : 76;
  const toBearing = (v) => v + (absolute ? heading : 0);
  let out =
    defs(o) +
    dial(o, {
      cx,
      cy,
      r,
      rotation: absolute ? 0 : heading === null ? 0 : -heading,
      cardinals: heading !== null || absolute,
      labels: true,
    });
  const trueAngle = angle(data, "trueAngle");
  const pointers = o.pointers ?? [
    { role: "apparentAngle", style: "needle", badge: true },
    { role: "trueAngle", style: "chevron", badge: true },
  ];
  // The dial's corner labels sit beside its scale, not in the face's corners,
  // so a face drawn to its content (web.js fitVisual) is mostly dial (TOM-606).
  const edge = r + 28,
    badgeRows = pointers.some((p, k) => p.badge && k >= 2 && k < 4) ? 2 : 1;
  out += angularZones(cx, cy, r - 3, o.sectors, absolute ? heading : 0);
  pointers.forEach((p, k) => {
    const v = angle(data, p.role);
    if (v === null) return;
    const bearing = toBearing(v),
      color = p.role === "apparentAngle" ? T : p.role === "trueAngle" ? A : M,
      radius = p.role === "apparentAngle" ? r - 17 : r,
      name = p.role === "apparentAngle" ? "needle" : "pointer";
    if (kind === "petal") {
      out += `<g transform="translate(${cx} ${cy}) rotate(${bearing})" ${motion}><path d="M0 -42 C-24 -59 -19 -${radius + 12} 0 -${radius + 14} C19 -${radius + 12} 24 -59 0 -42Z" fill="${color}" opacity="${p.role === "apparentAngle" ? 0.7 : 0.85}" ${part(name)}/></g>`;
    } else {
      if (["halo", "tactical"].includes(kind))
        out += ribbon(
          cx,
          cy,
          42,
          radius + 6,
          bearing,
          kind === "halo" ? 14 : 9,
          color,
          0.12,
        );
      if (["sail", "orbit"].includes(kind))
        out += path(
          arc(cx, cy, radius, bearing - (kind === "sail" ? 34 : 25), bearing),
          color,
          kind === "sail" ? 7 : 4,
          part("ribbon", 'opacity=".5"'),
        );
      out += marker(
        cx,
        cy,
        radius,
        bearing,
        p.style,
        color,
        simple(o) ? 0.9 : 1.1,
        name,
      );
    }
    if (p.badge && k < 4) {
      const x = side ? 252 : k % 2 ? cx + edge : cx - edge,
        y = side
          ? 226 + Math.floor(k / 2) * 14
          : cy + r + 22 + Math.floor(k / 2) * 12;
      out += text(
        x,
        y,
        `${p.role === "apparentAngle" ? "AWA" : p.role === "trueAngle" ? "TWA" : short(p.role, 7)} ${absolute ? `${Math.round(normalized(bearing)) % 360}°` : signedLabel(signedAngle(v))}`,
        9,
        color,
        side ? "start" : k % 2 ? "end" : "start",
        part("secondary"),
      );
    }
  });
  if (kind === "tactical") {
    const trail = data.history?.trueAngle?.points ?? [];
    trail.slice(-12).forEach((p, k, points) => {
      if (!numeric(p.value) || ["missing", "disconnected"].includes(p.quality))
        return;
      const v = p.unit === "rad" ? convert(p.value, "rad", "deg") : p.value;
      const q = point(cx, cy, r - 3 - (points.length - k) * 0.65, toBearing(v));
      out += circle(
        q.x,
        q.y,
        1.8,
        A,
        "none",
        0,
        part("history-dot", `opacity="${0.1 + (0.5 * k) / points.length}"`),
      );
    });
  }
  if (kind === "sail") {
    out += path(
      `M${cx} ${cy - 40} C${cx - 15} ${cy - 13} ${cx - 16} ${cy + 25} ${cx - 10} ${cy + 43} Q${cx} ${cy + 52} ${cx + 10} ${cy + 43} C${cx + 16} ${cy + 25} ${cx + 15} ${cy - 13} ${cx} ${cy - 40}Z`,
      M,
      1.3,
      part("boat"),
    );
    out += `<path d="M${cx - 3} ${cy - 31} C${cx - 12} ${cy - 13} ${cx - 29} ${cy + 8} ${cx - 30} ${cy + 20} H${cx - 3}Z" fill="${A}" fill-opacity=".18" stroke="${A}" stroke-width="1" ${part("sail")}/>`;
    out += `<path d="M${cx + 3} ${cy - 24} L${cx + 25} ${cy + 17} H${cx + 3}Z" fill="${T}" fill-opacity=".05" stroke="${M}" stroke-width="1" ${part("sail")}/>`;
    out +=
      centerReading(data, "trueSpeed", o, 302, 110, "TRUE WIND", 35) +
      centerReading(
        data,
        "apparentSpeed",
        o,
        302,
        179,
        "APPARENT",
        35,
        "secondary",
      );
  } else if (kind === "tactical") {
    out +=
      circle(cx, cy, 44, B, G, 0.6, part("face")) +
      text(
        cx,
        cy - 16,
        "TRUE ANGLE",
        7.5,
        M,
        "middle",
        part("caption", 'letter-spacing="1.2"'),
      );
    out += text(
      cx,
      cy + 13,
      trueAngle === null
        ? "—"
        : `${Math.abs(signedAngle(trueAngle)).toFixed(0)}°`,
      32,
      T,
      "middle",
      part("value", bold),
    );
    out += text(
      cx,
      cy + 31,
      trueAngle === null
        ? "UNAVAILABLE"
        : signedAngle(trueAngle) < 0
          ? "PORT"
          : "STARBOARD",
      8,
      A,
      "middle",
      part("caption"),
    );
    out +=
      centerReading(
        data,
        "trueSpeed",
        o,
        302,
        110,
        "TRUE WIND",
        32,
        "secondary",
      ) +
      centerReading(
        data,
        "apparentSpeed",
        o,
        302,
        179,
        "APPARENT",
        32,
        "secondary",
      );
  } else {
    out += circle(
      cx,
      cy,
      43,
      B,
      kind === "halo" ? G : "none",
      0.6,
      part("face"),
    );
    out += centerReading(
      data,
      o.centerRole,
      o,
      cx,
      cy - 3,
      o.centerRole === "trueSpeed" ? "TRUE WIND" : "APPARENT",
      simple(o) ? 40 : 37,
    );
    if (o.speedLabels) {
      const secondary =
          o.centerRole === "trueSpeed" ? "apparentSpeed" : "trueSpeed",
        f = reading(data, secondary, o);
      out += text(
        cx,
        cy + 40,
        `${secondary === "trueSpeed" ? "TWS" : "AWS"} ${f.value} ${f.unit}`,
        9,
        M,
        "middle",
        part("secondary"),
      );
    }
  }
  out += text(
    cx - edge,
    cy - r - 13,
    absolute ? "NORTH UP" : "BOW UP",
    8,
    M,
    "start",
    part("caption", 'letter-spacing="1.8"'),
  );
  if (o.speedLabels && side) out += line(237, 79, 237, 213, G, 1, part("rule"));
  // Under the dial's bottom label (it was drawn over "180") and its badges.
  out += text(
    cx,
    cy + r + 36 + (side ? 0 : (badgeRows - 1) * 12),
    short(
      referenceLabel(data, ["apparentAngle", "trueAngle", "heading"]) ||
        "Wind reference unavailable",
      54,
    ),
    8.5,
    M,
    "middle",
    part("caption"),
  );
  return out;
}

function closeWind(data, o) {
  const v = angle(data, "angle");
  if (v === null) return missing("Wind angle unavailable");
  const [min, max] = o.expandedArc,
    signed = signedAngle(v),
    projected =
      Math.sign(signed) *
      clamp((Math.abs(signed) - min) / (max - min), 0, 1) *
      80;
  const cx = 200,
    cy = 218,
    r = simple(o) ? 110 : 115;
  let out = defs(o) + path(arc(cx, cy, r, -80, 80), G, simple(o) ? 4 : 10);
  for (let k = -4; k <= 4; k++) {
    const p = point(cx, cy, r + 6, k * 20),
      q = point(cx, cy, r + (k % 2 ? 10 : 15), k * 20);
    out += line(p.x, p.y, q.x, q.y, M, 1);
    if (!simple(o) && k) {
      const t = point(cx, cy, r + 24, k * 20);
      out += text(
        t.x,
        t.y + 3,
        Math.round(min + (Math.abs(k) / 4) * (max - min)),
        8,
        M,
        "middle",
      );
    }
  }
  const target = clamp((o.targetAngle - min) / (max - min), 0, 1) * 80;
  for (const sign of [-1, 1])
    out += marker(cx, cy, r, target * sign, "tick", M, 0.55, "reference");
  if (o.historyTrail === "short")
    (data.history?.angle?.points ?? []).slice(-10).forEach((p, k) => {
      if (!numeric(p.value) || ["missing", "disconnected"].includes(p.quality))
        return;
      const a = p.unit === "rad" ? convert(p.value, "rad", "deg") : p.value;
      const projected =
          Math.sign(a) * clamp((Math.abs(a) - min) / (max - min), 0, 1) * 80,
        q = point(cx, cy, r - 2, projected);
      out += circle(q.x, q.y, 2, A, "none", 0, `opacity="${0.12 + k * 0.06}"`);
    });
  out +=
    ribbon(cx, cy, r - 10, r + 3, projected, 8, A, 0.24) +
    marker(cx, cy, r, projected, o.pointerStyle, A, 1.1);
  out += text(
    200,
    200,
    `${Math.abs(signed).toFixed(1)}°`,
    47,
    T,
    "middle",
    bold,
  );
  if (o.tackLabels)
    out +=
      text(60, 232, "PORT", 9, M) + text(340, 232, "STARBOARD", 9, M, "end");
  const f = reading(data, "speed", o);
  out += text(
    200,
    224,
    f.quality === "missing"
      ? `${signed < 0 ? "Port" : "Starboard"} tack`
      : `${f.value} ${f.unit} · ${signed < 0 ? "Port" : "Starboard"} tack`,
    10,
    M,
    "middle",
  );
  out += text(
    200,
    252,
    `Expanded ${min}–${max}° · Target ${o.targetAngle}°`,
    9,
    M,
    "middle",
  );
  return out;
}

function windAngle(data, o) {
  const v = angle(data, "angle");
  if (v === null) return missing("Wind angle unavailable");
  let out = defs(o) + dial(o, { labels: false, cardinals: false });
  out +=
    angularZones(200, 153, 76, o.targetSectors) +
    ribbon(200, 153, 48, 76, v, simple(o) ? 7 : 14, A, 0.15) +
    marker(200, 153, 77, v, o.pointerStyle, A, 1.1, "needle");
  out +=
    o.center === "speed"
      ? centerReading(data, "speed", o, 200, 151, "WIND SPEED", 41)
      : text(
          200,
          164,
          `${Math.abs(signedAngle(v)).toFixed(1)}°`,
          44,
          T,
          "middle",
          part("value", bold),
        ) +
        text(
          200,
          190,
          signedAngle(v) < 0 ? "PORT" : "STARBOARD",
          9,
          M,
          "middle",
          part("caption", 'letter-spacing="1.6"'),
        );
  out += text(
    200,
    59,
    "BOW",
    8,
    M,
    "middle",
    part("caption", 'letter-spacing="2"'),
  );
  if (o.portStarboardLabels)
    out +=
      text(91, 155, "P", 11, M, "middle", part("caption")) +
      text(309, 155, "S", 11, M, "middle", part("caption"));
  out += text(
    200,
    253,
    short(
      referenceLabel(data, ["angle"]) ||
        `Relative to bow · ${o.bounds[0]}° to ${o.bounds[1]}°`,
      50,
    ),
    9,
    M,
    "middle",
    part("caption"),
  );
  return out;
}

function balanceFace(id, data, o) {
  const role =
    id === "rudder"
      ? "actualAngle"
      : id === "turn-rate"
        ? "turnRate"
        : o.axis === "pitch"
          ? "pitch"
          : "roll";
  let v = id === "turn-rate" ? numberValue(data, role) : angle(data, role);
  if (v === null) return missing("Angle or rate unavailable");
  if (id === "turn-rate" && Math.abs(v) < o.deadband) v = 0;
  const range = o.bounds ?? o.range ?? o.scale,
    [min, max] = range,
    fraction = clamp((v - min) / (max - min), 0, 1),
    x = 47 + fraction * 306;
  let out = defs(o),
    f = reading(
      {
        ...data,
        values: { ...data.values, [role]: { ...sample(data, role), value: v } },
      },
      role,
      o,
    ),
    label =
      id === "rudder"
        ? "RUDDER ANGLE"
        : id === "turn-rate"
          ? "RATE OF TURN"
          : role.toUpperCase();
  if (id === "heel" && o.display === "bubble") {
    const pitch = angle(data, "pitch"),
      px = (clamp(v, -45, 45) / 45) * 55,
      py = pitch === null ? 0 : (clamp(pitch, -45, 45) / 45) * 55;
    out +=
      circle(153, 151, 76, simple(o) ? "none" : S, G, 1.5) +
      circle(153, 151, 49, "none", G, 1);
    out += line(94, 151, 212, 151, G) + line(153, 92, 153, 210, G);
    out += circle(153 + px, 151 + py, 12, A, B, 3, motion);
    out +=
      text(282, 130, `${v.toFixed(1)}°`, 36, T, "middle", bold) +
      text(282, 155, label, 9, M, "middle");
    if (o.axis === "both")
      out += text(
        282,
        188,
        `Pitch ${reading(data, "pitch", o).value}°`,
        10,
        M,
        "middle",
      );
    out += text(
      200,
      247,
      pitch === null
        ? "Pitch unavailable · roll shown"
        : short(o.zeroReference, 50),
      9,
      M,
      "middle",
    );
    return out;
  }
  out += text(
    200,
    75,
    label,
    9,
    M,
    "middle",
    part("caption", 'letter-spacing="1.7"'),
  );
  out += text(
    200,
    122,
    `${id === "turn-rate" ? f.value : v.toFixed(1)}${id === "turn-rate" ? "" : "°"}`,
    simple(o) ? 51 : 46,
    T,
    "middle",
    part("value", bold),
  );
  if (id === "turn-rate")
    out += text(200, 143, f.unit, 10, M, "middle", part("unit"));
  out += rect(47, 172, 306, simple(o) ? 7 : 16, G, 8, part("track"));
  const zero = 47 + clamp((0 - min) / (max - min), 0, 1) * 306;
  if (!simple(o))
    out += rect(
      Math.min(zero, x),
      172,
      Math.abs(x - zero),
      16,
      `url(#${svgId(o, "instrument")}-track)`,
      8,
      part("fill", motion),
    );
  if (id !== "turn-rate" || o.centerMark)
    out += line(zero, 164, zero, 194, M, 1, part("reference"));
  if (!simple(o))
    for (let k = 0; k <= 8; k++)
      out += line(
        47 + k * 38.25,
        199,
        47 + k * 38.25,
        k % 2 ? 203 : 207,
        G,
        1,
        part("ticks"),
      );
  out +=
    id === "turn-rate" && o.pointerStyle === "bar"
      ? rect(
          Math.min(zero, x),
          172,
          Math.abs(x - zero),
          simple(o) ? 7 : 16,
          A,
          8,
          part("fill", motion),
        )
      : `<g transform="translate(${x} 179)" ${motion}><path d="M0 -14 C-9 -9 -12 -3 -10 5 Q0 13 10 5 C12 -3 9 -9 0 -14Z" fill="${A}" ${part("pointer")}/><circle cy="0" r="3" fill="${B}" ${part("pointer")}/></g>`;
  if (id !== "rudder" || o.portStarboardLabels)
    out +=
      text(
        47,
        225,
        id === "turn-rate" ? min : "PORT",
        9,
        M,
        "start",
        part("low"),
      ) +
      text(
        353,
        225,
        id === "turn-rate" ? max : "STARBOARD",
        9,
        M,
        "end",
        part("high"),
      );
  if (id === "rudder") {
    const order = angle(data, "orderedAngle");
    if (o.showOrder && order !== null) {
      const ox = 47 + clamp((order - min) / (max - min), 0, 1) * 306;
      out +=
        marker(
          ox,
          197,
          0,
          0,
          o.orderStyle,
          "var(--attention)",
          0.65,
          "reference",
        ) +
        text(
          200,
          252,
          `Ordered ${order.toFixed(1)}°${o.zeroLabel ? ` · ${o.zeroLabel}` : ""}`,
          9,
          M,
          "middle",
          part("secondary"),
        );
    } else if (o.zeroLabel)
      out += text(200, 252, o.zeroLabel, 9, M, "middle", part("caption"));
  } else if (id === "heel") {
    const limit = Math.abs(v) > o.visualLimit;
    out += text(
      200,
      251,
      limit
        ? `Visual guide ${o.visualLimit}° exceeded`
        : o.axis === "both"
          ? `Pitch ${reading(data, "pitch", o).value}° · ${o.zeroReference}`
          : o.zeroReference,
      9,
      limit ? "var(--attention)" : M,
      "middle",
      part("caption"),
    );
  } else if (!simple(o))
    out += text(
      200,
      252,
      o.deadband
        ? `Measured · ${o.deadband} ${f.unit} deadband`
        : referenceLabel(data, [role]),
      9,
      M,
      "middle",
      part("caption"),
    );
  return out;
}

function attitudeFace(data, o) {
  const roll = angle(data, "roll"),
    pitch = angle(data, "pitch");
  if (roll === null || pitch === null)
    return missing("Roll and pitch required");
  const id = svgId(o, "horizon"),
    shift = clamp(pitch, -45, 45) * 1.55;
  let out =
    defs(o) +
    `<defs><clipPath id="${id}"><circle cx="178" cy="151" r="81"/></clipPath></defs><g clip-path="url(#${id})"><g transform="rotate(${-roll} 178 151)" ${motion}>${rect(85, 60 + shift, 186, 91, "var(--sky)", 0)}${rect(85, 151 + shift, 186, 170, "var(--earth)", 0)}`;
  if (o.horizon) out += line(85, 151 + shift, 271, 151 + shift, T, 1.5);
  if (o.pitchLadder)
    for (const v of [-20, -10, 10, 20])
      out +=
        line(
          178 - (v % 20 ? 17 : 26),
          151 + shift + v * 1.55,
          178 + (v % 20 ? 17 : 26),
          151 + shift + v * 1.55,
          T,
          0.8,
          'opacity=".55"',
        ) +
        (!simple(o)
          ? text(213, 154 + shift + v * 1.55, Math.abs(v), 8, T)
          : "");
  out += "</g></g>" + circle(178, 151, 82, "none", G, 2);
  for (const tick of o.rollTicks) {
    const p = point(178, 151, 89, tick),
      q = point(178, 151, 94, tick);
    out += line(p.x, p.y, q.x, q.y, M, tick === 0 ? 2 : 1);
  }
  out += marker(178, 151, 89, roll, "chevron", T, 0.7);
  out +=
    path("M138 151 H160 L178 158 L196 151 H218", A, 3.5) +
    circle(178, 151, 2, A, "none");
  out +=
    text(303, 126, `${roll.toFixed(1)}°`, 30, T, "middle", bold) +
    text(303, 145, "ROLL", 8, M, "middle", 'letter-spacing="1.5"') +
    text(303, 187, `${pitch.toFixed(1)}°`, 27, T, "middle", bold) +
    text(303, 204, "PITCH", 8, M, "middle", 'letter-spacing="1.5"');
  if (o.headingStrip)
    out += pill(
      99,
      238,
      158,
      `HDG ${reading(data, "heading", o).value} ${referenceLabel(data, ["heading"])}`,
    );
  else
    out += text(
      200,
      251,
      short(
        referenceLabel(data, ["roll", "pitch"]) || "Source calibration",
        45,
      ),
      9,
      M,
      "middle",
    );
  return out;
}

function setDrift(data, o) {
  const set = angle(data, "set"),
    drift = numberValue(data, "drift"),
    heading = angle(data, "heading");
  if (set === null || drift === null)
    return missing("Set and drift unavailable");
  if (o.orientation === "heading-up" && heading === null)
    return missing("Compatible heading source required");
  const bearing = set - (o.orientation === "heading-up" ? heading : 0),
    length =
      o.vectorScale === "fixed"
        ? clamp(drift / Math.max(0.01, o.scaleMax), 0, 1) * 63
        : clamp(19 + drift * 13, 0, 63),
    q = point(144, 151, length, bearing);
  let out =
    defs(o) +
    dial(o, {
      cx: 144,
      cy: 151,
      r: 73,
      rotation: o.orientation === "heading-up" ? -heading : 0,
    });
  out +=
    circle(144, 151, 49, "none", G, 0.75) +
    circle(144, 151, 24, "none", G, 0.75);
  out += `<g transform="translate(144 151) rotate(${bearing})" ${motion}><path d="M-4 0 L-10 -${Math.max(0, length - 9)} L0 -${length} L10 -${Math.max(0, length - 9)} L4 0Z" fill="${A}" fill-opacity=".72"/></g>`;
  out += circle(144, 151, 5, B, T, 1.5) + circle(q.x, q.y, 3, T, A, 2, motion);
  out +=
    text(
      302,
      119,
      `${Math.round(normalized(set)) % 360}°`,
      35,
      T,
      "middle",
      bold,
    ) +
    text(302, 137, "SET", 8, M, "middle", 'letter-spacing="1.7"') +
    centerReading(data, "drift", o, 302, 185, "DRIFT", 31);
  if (o.showReference)
    out += text(
      200,
      241,
      short(
        referenceLabel(data, ["set", "drift"]) || "Reference unavailable",
        47,
      ),
      8.5,
      M,
      "middle",
    );
  if (o.showConfidence)
    out += text(
      200,
      254,
      short(data.provenance?.confidence ?? "Confidence unavailable", 43),
      8,
      M,
      "middle",
    );
  return out;
}

function displayNumber(data, role, o) {
  const raw = numberValue(data, role),
    source = sample(data, role),
    unit = o.roleUnits?.[role] ?? o.unit;
  if (raw === null) return null;
  if (unit && unit !== "inherit" && source?.unit) {
    if (
      !dimensionForUnit(unit) ||
      dimensionForUnit(unit) !== dimensionForUnit(source.unit)
    )
      return o.roleUnits?.[role] ? null : raw;
    return convert(raw, source.unit, unit);
  }
  return raw;
}

function historyPoints(data, role, o) {
  const series = data.history?.[role];
  if (!series?.points?.length) return [];
  const unit = reading(data, role, o).unit || series.unit;
  const window =
    { "1h": 3600000, "24h": 86400000, "7d": 604800000, "30d": 2592000000 }[
      o.window ?? o.historyWindow
    ] ?? 86400000;
  const end = numeric(data.now)
    ? data.now
    : Math.max(
        ...series.points.map((p) => (numeric(p.time) ? p.time : -Infinity)),
      );
  const list = series.points
    .filter((p) => numeric(p.time) && p.time >= end - window && p.time <= end)
    .map((p) => {
      if (!numeric(p.value) || ["missing", "disconnected"].includes(p.quality))
        return { ...p, value: null };
      try {
        return {
          ...p,
          value:
            unit && series.unit ? convert(p.value, series.unit, unit) : p.value,
        };
      } catch {
        return { ...p, value: null };
      }
    });
  return smoothSeries(list, o.smoothing, o.aggregation === "circular");
}

function trace(
  points,
  o,
  { x = 32, y = 201, w = 336, h = 38, fill = false, labels = false } = {},
) {
  const valid = points.filter((p) => numeric(p.value));
  if (!valid.length)
    return text(
      x,
      y + h / 2,
      "History unavailable",
      9,
      M,
      "start",
      part("caption"),
    );
  let lo = Math.min(...valid.map((p) => p.value)),
    hi = Math.max(...valid.map((p) => p.value));
  if (o.scaleMode === "fixed") [lo, hi] = [o.scaleMin, o.scaleMax];
  if (hi <= lo) {
    const pad = Math.max(1, Math.abs(lo) * 0.02);
    lo -= pad;
    hi += pad;
  }
  const times = points.map((p) => p.time),
    t0 = Math.min(...times),
    t1 = Math.max(...times);
  const coord = (p) => ({
    x: x + (t1 === t0 ? 0.5 : (p.time - t0) / (t1 - t0)) * w,
    y: y + h - clamp((p.value - lo) / (hi - lo), 0, 1) * h,
  });
  let out = "",
    d = "",
    first = null,
    last = null;
  const finish = () => {
    if (!d) return;
    if (fill && first && last)
      out += `<path d="${d} L${last.x} ${y + h} L${first.x} ${y + h}Z" fill="${A}" fill-opacity=".09" ${part("history-area")}/>`;
    out += path(d, A, simple(o) ? 1.75 : 2.5, part("history-line", motion));
    d = "";
    first = null;
    last = null;
  };
  for (const p of points) {
    if (!numeric(p.value)) {
      finish();
      continue;
    }
    const q = coord(p);
    if (!d) {
      d = `M${q.x} ${q.y}`;
      first = q;
    } else if (o.curve === "step") d += ` H${q.x} V${q.y}`;
    else if (o.curve === "smooth" && last)
      d += ` C${(last.x + q.x) / 2} ${last.y} ${(last.x + q.x) / 2} ${q.y} ${q.x} ${q.y}`;
    else d += ` L${q.x} ${q.y}`;
    last = q;
  }
  finish();
  const q = coord(valid.at(-1));
  out += circle(
    q.x,
    q.y,
    simple(o) ? 3 : 4,
    A,
    B,
    1.5,
    part("history-dot", motion),
  );
  if (labels)
    out += text(
      x,
      y + h + 15,
      `${lo.toFixed(1)} — ${hi.toFixed(1)} range`,
      8.5,
      M,
      "start",
      part("caption"),
    );
  return out;
}

/**
 * Whether a number draws the readout (TOM-956, cloud ADR 0460): a Detailed
 * number whose look names no design of its own. A panel that names one
 * (odometer, ribbon, horizon, focus, line, capsule) keeps it, the Simple set
 * keeps its focus design, and a trend number is unchanged.
 */
export function numberReadout(definitionId, options = {}) {
  return (
    definitionId === "number" &&
    !simple(options ?? {}) &&
    (!options?.readoutDesign || options.readoutDesign === "auto")
  );
}

// The readout's unit, and each word of a duration, at this share of the
// value's size (the mockup Mike approved, TOM-956).
const READOUT_UNIT = 0.42;

/**
 * A reading as the readout draws it: its numbers large and its words small.
 * A duration ("6 h 40 min", TOM-742) arrives as words with no unit; any other
 * reading is one number and its unit. Each part after the first starts with
 * a space inside the part, so the space takes that part's size and the text
 * reads "246 V" to a screen reader; a unit that hugs its number ("%", "°F")
 * has none, and a bare "°" is drawn at the number's size, as an angle reads.
 * The large parts are the text's own characters and each small one a
 * `<tspan>` named `unit`: a page's CSS can restyle units, and SVG transforms
 * no tspan, so a large part needs no name or origin of its own.
 */
export function readoutParts(value, unit = "") {
  const number = /^[<>]?[−+-]?[\d.,]+°?$|^—$/;
  const parts = String(value ?? "")
    .split(" ")
    .filter((word) => word !== "")
    .map((word) => ({ text: word, large: number.test(word) }));
  // A reading with no number in it ("Unit mismatch", a word) is drawn large.
  if (!parts.some((p) => p.large)) for (const p of parts) p.large = true;
  if (unit === "°" && parts.length) parts[parts.length - 1].text += "°";
  else if (unit) parts.push({ text: unit, large: false });
  return parts.map((p, k) => ({
    ...p,
    text:
      k === 0 || (!p.large && (p.text === "%" || p.text.startsWith("°")))
        ? p.text
        : ` ${p.text}`,
  }));
}

/** About how wide the readout's parts are, in ems of the value's size. */
function readoutEm(parts) {
  let em = 0;
  for (const { text: words, large } of parts) {
    let w = 0;
    for (const c of words) {
      if (/[0-9]/.test(c)) w += 0.62;
      else if (".,:".includes(c)) w += 0.28;
      else if ("−+-".includes(c)) w += 0.62;
      else if (c === "—") w += 0.92;
      else if (c === " ") w += 0.25;
      else if (c === "°") w += 0.4;
      else if (c === "%") w += 0.86;
      else if ("mwMW".includes(c)) w += 0.88;
      else if ("iljtf".includes(c)) w += 0.3;
      else if (/[A-Z]/.test(c)) w += 0.66;
      else w += 0.57;
    }
    em += large ? w : w * READOUT_UNIT;
  }
  return em;
}

/**
 * The readout (TOM-956, cloud ADR 0460): a number's design when its look
 * names none. The value large in the rounded numeric font, weight 700, its
 * unit beside it at 42% of its size, weight 600, muted; no plate, reel or
 * rule. A stale reading, and the dash for none, are quiet. It is fitted to
 * its digits (fit.js, `data-fit="digits"`), so in a panel it is as large as
 * the panel allows, centred. It never counts from one reading to the next
 * (web.js, page.js): Marina §9 updates a reading without a rolling animation.
 */
function numberReadoutFace(data, f, o, anchor) {
  const parts = readoutParts(f.value, f.unit);
  const grey = ["stale", "missing", "disconnected"].includes(f.quality);
  const Q = "var(--quiet)";
  // Large enough to draw crisply, small enough that a long reading and its
  // fit's margins stay inside the 400 × 300 face.
  const size = Math.round(Math.min(72, 320 / Math.max(1, readoutEm(parts))));
  const second =
    o.secondaryRole && sample(data, o.secondaryRole)
      ? reading(data, o.secondaryRole, o)
      : null;
  // The digits centred on the face's middle, or a little above it to leave
  // room for a second reading under them.
  const baseline = Math.round(150 + size * (second ? 0.15 : 0.35));
  const x = anchor === "start" ? 34 : anchor === "end" ? 366 : 200;
  const font =
    'style="font-family:var(--tc-font-rounded,var(--tc-font-sans,TCInter,sans-serif))"';
  const unitSize = Math.round(size * READOUT_UNIT * 10) / 10;
  const spans = parts
    .map(({ text: words, large }) =>
      large
        ? E(words)
        : `<tspan font-size="${unitSize}" font-weight="600" fill="${grey ? Q : M}" ${part("unit")}>${E(words)}</tspan>`,
    )
    .join("");
  let out = `<text x="${x}" y="${baseline}" fill="${grey ? Q : T}" font-size="${size}" font-weight="700" text-anchor="${anchor}" ${font} ${part("value", 'data-fit="digits"')}>${spans}</text>`;
  if (second)
    out += text(
      x,
      Math.round(baseline + size * 0.55),
      `${second.value} ${second.unit}`.trim(),
      Math.round(size * 0.3),
      grey ? Q : M,
      anchor,
      `${font} font-weight="600" ${part("secondary")}`,
    );
  return out;
}

function numberFace(id, data, o) {
  const role = "value",
    f = reading(data, role, o),
    v = displayNumber(data, role, o),
    isTrend = id === "trend-number";
  const design = numberReadout(id, o)
    ? "readout"
    : o.readoutDesign && o.readoutDesign !== "auto"
      ? o.readoutDesign
      : simple(o)
        ? isTrend
          ? "line"
          : "focus"
        : "horizon";
  const align =
      o.alignment === "start"
        ? "start"
        : o.alignment === "end"
          ? "end"
          : "middle",
    anchor = isTrend ? "start" : align;
  if (design === "readout") return numberReadoutFace(data, f, o, anchor);
  const x = anchor === "start" ? 34 : anchor === "end" ? 366 : 200,
    size = o.valueSize === "normal" ? 45 : 67;
  const points = historyPoints(data, role, o),
    measured = points.filter((p) => numeric(p.value));
  let out = defs(o),
    valueY = design === "capsule" ? 158 : 156;
  if (design === "capsule")
    out +=
      rect(32, 91, 336, 112, S, 28, part("face", `stroke="${G}"`)) +
      line(50, 100, 70, 100, A, 2, part("rule", 'opacity=".65"'));
  if (
    design === "odometer" &&
    v !== null &&
    // Reels hold digits: a duration ("1 min 30 s", TOM-742) or any other
    // words draw as plain text.
    /^[−+-]?[\d.,]+$/.test(f.value) &&
    f.value.length <= 10
  ) {
    const chars = [...f.value],
      digitWidth = chars.length > 7 ? 29 : 40,
      width = chars.reduce(
        (w, c) => w + (/[0-9]/.test(c) ? digitWidth : digitWidth * 0.42),
        0,
      ),
      begin =
        anchor === "middle" ? x - width / 2 : anchor === "end" ? x - width : x;
    let position = begin;
    const clip = svgId(o, "reels");
    const precision = f.value.includes(".") ? f.value.split(".")[1].length : 0,
      scaled = Math.abs(v) * 10 ** precision,
      rolling = o._motionPhase === "animating",
      pitch = 75;
    out += `<defs><clipPath id="${clip}">${rect(begin, 90, width, 81, "white", 14)}</clipPath><linearGradient id="${clip}-fade" x1="0" y1="0" x2="0" y2="1"><stop stop-color="${S}"/><stop offset=".24" stop-color="${S}" stop-opacity="0"/><stop offset=".76" stop-color="${S}" stop-opacity="0"/><stop offset="1" stop-color="${S}"/></linearGradient></defs>`;
    chars.forEach((c, k) => {
      const w = /[0-9]/.test(c) ? digitWidth : digitWidth * 0.42;
      if (/[0-9]/.test(c)) {
        const place =
            10 **
            chars.slice(k + 1).filter((digit) => /[0-9]/.test(digit)).length,
          digit = rolling ? Math.floor(scaled / place) % 10 : Number(c),
          fraction = rolling ? clamp((scaled % place) - (place - 1), 0, 1) : 0;
        out += rect(
          position + 1,
          90,
          w - 2,
          82,
          S,
          10,
          part("face", `stroke="${G}"`),
        );
        out += `<g clip-path="url(#${clip})"><g transform="translate(0 ${-fraction * pitch})" data-reel="${k}" data-motion="reel">`;
        for (const offset of [-1, 0, 1])
          out += text(
            position + w / 2,
            150 + offset * pitch,
            (digit + offset + 10) % 10,
            Math.min(chars.length > 7 ? 50 : 62, size),
            T,
            "middle",
            part("value", bold),
          );
        out +=
          "</g></g>" +
          rect(
            position + 2,
            91,
            w - 4,
            79,
            `url(#${clip}-fade)`,
            9,
            part("fade"),
          );
      } else
        out += text(
          position + w / 2,
          153,
          c,
          49,
          M,
          "middle",
          part("value", bold),
        );
      position += w;
    });
    out +=
      line(
        begin + 6,
        174,
        begin + width - 6,
        174,
        A,
        1.5,
        part("rule", 'opacity=".55"'),
      ) + text(x, 198, f.unit, 11, M, anchor, part("unit"));
  } else {
    out += text(
      x,
      valueY,
      f.value,
      Math.min(
        design === "capsule" ? Math.min(size, 58) : size,
        330 / Math.max(1, String(f.value).length * 0.56),
      ),
      // A number keeps its face with no reading (TOM-956): its dash is muted.
      ["stale", "missing", "disconnected"].includes(f.quality) ? M : T,
      anchor,
      part("value", bold),
    );
    out += text(
      x,
      valueY + 25,
      f.unit,
      12,
      M,
      anchor,
      part("unit", 'letter-spacing="1"'),
    );
    if (design === "focus")
      out += line(
        x - (anchor === "end" ? 46 : anchor === "middle" ? 23 : 0),
        216,
        x + (anchor === "start" ? 46 : anchor === "middle" ? 23 : 0),
        216,
        A,
        2,
        part("rule", 'opacity=".6"'),
      );
  }
  if (["horizon", "line"].includes(design) || isTrend) {
    const behind = isTrend && o.historyPlacement === "behind";
    const chart = trace(points, o, {
      x: 34,
      y: behind ? 93 : 210,
      w: 332,
      h: behind ? 80 : o.sparkHeight === "compact" ? 19 : 31,
      fill: design === "horizon",
      labels: false,
    });
    out = behind
      ? out.replace(defs(o), defs(o) + `<g opacity=".33">${chart}</g>`)
      : out + chart;
  } else if (design === "ribbon") {
    if (!measured.length)
      out += text(
        34,
        241,
        "History unavailable",
        9,
        M,
        "start",
        part("caption"),
      );
    else {
      const vals = measured.slice(-24),
        low = Math.min(...vals.map((p) => p.value)),
        high = Math.max(...vals.map((p) => p.value)),
        span = high - low || 1;
      vals.forEach((p, k) => {
        const h = 10 + ((p.value - low) / span) * 26,
          w = 332 / vals.length;
        out += rect(
          34 + k * w,
          243 - h,
          w - 3,
          h,
          A,
          2,
          part(
            "history-area",
            `opacity="${0.18 + (0.48 * (k + 1)) / vals.length}"`,
          ),
        );
      });
      out += text(
        34,
        254,
        `${low.toFixed(1)} — ${high.toFixed(1)} recent range`,
        8,
        M,
        "start",
        part("caption"),
      );
    }
  }
  if (
    measured.length > 1 &&
    (isTrend ? o.delta !== "off" : ["capsule", "horizon"].includes(design))
  ) {
    const delta = measured.at(-1).value - measured[0].value;
    const percent = o.delta === "percent" && measured[0].value !== 0,
      label = `${delta >= 0 ? "+" : ""}${(percent ? (delta / Math.abs(measured[0].value)) * 100 : delta).toFixed(1)}${percent ? "%" : ` ${f.unit}`}`;
    out += text(
      366,
      design === "capsule" ? 229 : 75,
      label,
      11,
      A,
      "end",
      part("delta", motion),
    );
  }
  if (isTrend && o.extremes !== "off" && measured.length)
    out += text(
      34,
      254,
      `Range ${Math.min(...measured.map((p) => p.value)).toFixed(1)}–${Math.max(...measured.map((p) => p.value)).toFixed(1)} ${f.unit}`,
      8.5,
      M,
      "start",
      part("caption"),
    );
  if (o.secondaryRole && sample(data, o.secondaryRole)) {
    const secondary = reading(data, o.secondaryRole, o);
    out += text(
      366,
      254,
      `${secondary.value} ${secondary.unit}`,
      11,
      M,
      "end",
      part("secondary"),
    );
  }
  return out;
}

function stateFace(data, o) {
  const s = sample(data, "state"),
    known =
      s &&
      !["missing", "disconnected"].includes(s.quality) &&
      s.value !== null &&
      s.value !== undefined;
  const key = known ? String(s.value) : "unknown",
    mapped = o.mappings[key] ?? {
      label: known ? key : o.unknownLabel,
      tone: key === "true" ? "good" : key === "false" ? "muted" : "accent",
    },
    color = tone(mapped.tone);
  let out = defs(o);
  if (o.display === "dot") {
    out += simple(o)
      ? circle(200, 116, 12, color, "none", 0, motion)
      : circle(200, 118, 34, "none", color, 1, 'opacity=".2"') +
        circle(200, 118, 24, color, "none", 0, 'opacity=".12"') +
        circle(200, 118, 12, color, B, 3, motion);
  } else if (o.display === "symbol")
    out +=
      rect(171, 87, 58, 58, S, 18, `stroke="${G}"`) +
      text(
        200,
        128,
        key === "true" ? "✓" : key === "false" ? "○" : "?",
        33,
        color,
        "middle",
        motion,
      );
  else out += rect(60, 116, 280, 78, S, 24, simple(o) ? "" : `stroke="${G}"`);
  out += text(
    200,
    o.display === "label" ? 164 : 185,
    short(mapped.label, 24),
    32,
    color,
    "middle",
    'font-weight="600" data-motion="state"',
  );
  if (!simple(o))
    out += text(
      200,
      232,
      known ? "MEASURED STATE" : "STATE UNAVAILABLE",
      9,
      M,
      "middle",
      'letter-spacing="1.7"',
    );
  return out;
}

function textFace(data, o) {
  const datum = sample(data, "value"),
    raw =
      datum && !["missing", "disconnected"].includes(datum.quality)
        ? datum.value
        : null;
  const value =
      raw === null || raw === undefined
        ? o.emptyLabel
        : (o.enumLabels[String(raw)] ?? raw),
    words = String(value).split(/\s+/),
    lines = [];
  let current = "";
  for (const word of words) {
    if ((current + word).length > 23 && current) {
      lines.push(current);
      current = "";
    }
    current += (current ? " " : "") + word;
  }
  if (current) lines.push(current);
  const visible = o.wrap ? lines.slice(0, o.maxLines) : [short(value, 25)],
    center = o.alignment === "center",
    x = center ? 200 : 45;
  let out = defs(o);
  if (!simple(o))
    out +=
      rect(28, 80, 344, 148, S, 24, `stroke="${G}"`) +
      line(45, 96, 68, 96, A, 3);
  visible.forEach((s, k) => {
    out += text(
      x,
      157 - (visible.length - 1) * 19 + k * 38,
      s,
      28,
      T,
      center ? "middle" : "start",
      'font-weight="550" data-motion="text"',
    );
  });
  if (!simple(o))
    out += text(
      45,
      210,
      datum?.reference ?? "SOURCE TEXT",
      8,
      M,
      "start",
      'letter-spacing="1.1"',
    );
  return out;
}

function collectionFace(id, data, o) {
  const configured = o[id === "multi-text" ? "rows" : "cells"] ?? [],
    list = configured.length
      ? configured
      : Object.keys(data.values ?? data.service?.values ?? {})
          .slice(0, id === "multi-text" ? 6 : 8)
          .map((role) => ({ role, label: role, unit: "inherit", decimals: 1 }));
  if (!list.length) return missing("No bound readings");
  const cards = id === "status-strip" || o.layout === "two-column",
    cols = cards ? 2 : 1,
    rows = Math.ceil(list.length / cols),
    h = Math.min(cards ? 82 : 43, 186 / rows),
    begin = 62 + Math.max(0, (186 - h * rows) / 2);
  let out = defs(o);
  list.forEach((row, k) => {
    const x = 26 + (k % cols) * 176,
      y = begin + Math.floor(k / cols) * h,
      f = reading(data, row.role, {
        ...o,
        unit: row.unit ?? o.unit,
        roleUnits: { ...o.roleUnits, ...row.roleUnits },
        decimals: String(row.decimals ?? 1),
        roleDecimals: { ...o.roleDecimals, ...row.roleDecimals },
      }),
      label =
        o.labelMode === "short"
          ? short(row.label || row.role, 10)
          : short(row.label || row.role, cards ? 19 : 25);
    if (cards) {
      if (!simple(o)) out += rect(x, y, 170, h - 5, S, 15, `stroke="${G}"`);
      out +=
        text(x + 12, y + 18, label, 9, M) +
        text(
          x + 12,
          y + Math.min(h - 13, 49),
          `${f.value} ${f.unit}`,
          h < 55 ? 19 : 24,
          T,
          "start",
          'font-weight="600" data-motion="value"',
        );
      if (simple(o))
        out += line(x + 12, y + h - 7, x + 158, y + h - 7, G, 0.65);
    } else {
      if (!simple(o)) out += circle(x + 8, y + 17, 3, k === 0 ? A : M, "none");
      out +=
        text(x + (simple(o) ? 9 : 20), y + 22, label, 11, M) +
        text(
          362,
          y + 23,
          `${f.value} ${f.unit}`,
          18,
          T,
          "end",
          'font-weight="550" data-motion="value"',
        );
      if (o.separators || !simple(o))
        out += line(35, y + h - 3, 363, y + h - 3, G, 0.6);
    }
  });
  if (o.includeHealth)
    out += text(
      368,
      254,
      `Health · ${data.quality ?? "unknown"}`,
      8.5,
      data.quality === "good" ? M : "var(--attention)",
      "end",
    );
  return out;
}

function counterFace(data, o) {
  const n = numberValue(data, "count"),
    value = n === null ? "—" : String(Math.trunc(n)).padStart(o.digits, "0");
  let out = defs(o);
  if (!simple(o) && value.length <= 8) {
    const width = Math.min(43, 330 / value.length),
      start = 200 - (width * value.length) / 2;
    [...value].forEach((digit, k) => {
      out +=
        rect(start + k * width + 1, 91, width - 3, 84, S, 12, `stroke="${G}"`) +
        text(start + (k + 0.5) * width, 153, digit, 49, T, "middle", bold);
    });
    out += line(
      start + 7,
      179,
      start + width * value.length - 7,
      179,
      A,
      1.5,
      'opacity=".6"',
    );
  } else out += text(200, 164, value, 62, T, "middle", bold);
  out += text(
    200,
    211,
    o.countMode === "period" ? `${o.period} count` : "Total events",
    12,
    M,
    "middle",
  );
  if (o.showReset)
    out += pill(
      119,
      234,
      162,
      o.resetScope
        ? `Reset · ${short(o.resetScope, 19)}`
        : "Reset scope unavailable",
    );
  return out;
}

function timerFace(data, o) {
  const raw = numberValue(data, "duration"),
    source = sample(data, "duration");
  let seconds = raw;
  if (raw !== null && source?.unit && dimensionForUnit(source.unit) === "time")
    seconds = convert(raw, source.unit, "s");
  const running = val(data, "running"),
    textValue = durationText(seconds, o.format);
  let out = defs(o);
  if (!simple(o)) {
    const fraction =
      seconds === null ? 0 : normalized(Math.abs(seconds) * 6) / 360;
    out +=
      path(arc(200, 152, 86, -116, 116), G, 5) +
      (seconds !== null
        ? path(
            arc(200, 152, 86, -116, -116 + Math.max(0.001, fraction * 232)),
            A,
            5,
            motion,
          )
        : "");
    out += text(
      200,
      109,
      o.mode.toUpperCase(),
      8.5,
      M,
      "middle",
      'letter-spacing="1.8"',
    );
  }
  out += text(
    200,
    164,
    textValue,
    textValue.length > 6 ? 43 : 59,
    T,
    "middle",
    bold,
  );
  if (o.showRunningState)
    out +=
      circle(
        200 - (running === true ? 36 : 43),
        198,
        3,
        running === true ? A : M,
        "none",
      ) +
      text(
        204,
        202,
        running === true
          ? "Running"
          : running === false
            ? "Stopped"
            : "State unavailable",
        10,
        M,
        "middle",
      );
  out += text(200, 246, `${o.mode} · ${o.period}`, 9, M, "middle");
  return out;
}

function clockFace(id, data, o) {
  const timestamp = val(data, "time"),
    s = sample(data, "time"),
    date = new Date(timestamp);
  if (
    timestamp === null ||
    timestamp === undefined ||
    ["missing", "disconnected"].includes(s?.quality) ||
    !Number.isFinite(date.getTime())
  )
    return missing("Clock source unavailable");
  const zone = o.zone || o.timezone;
  let formatter, parts;
  try {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    parts = formatter.formatToParts(date);
  } catch {
    return missing("Clock timezone unavailable");
  }
  const part = (k) => Number(parts.find((p) => p.type === k)?.value ?? 0),
    h = part("hour"),
    m = part("minute"),
    seconds = part("second");
  let out = defs(o);
  if (id === "digital-clock") {
    const value = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      hour: "2-digit",
      minute: "2-digit",
      ...(o.seconds ? { second: "2-digit" } : {}),
      hour12: o.format === "12h",
    }).format(date);
    if (!simple(o))
      out +=
        rect(29, 90, 342, 105, S, 25, `stroke="${G}"`) +
        line(48, 201, 352, 201, A, 1.5, 'opacity=".35"');
    out += text(200, 158, value, value.length > 7 ? 45 : 66, T, "middle", bold);
    if (o.date)
      out += text(
        200,
        228,
        new Intl.DateTimeFormat("en-GB", {
          timeZone: zone,
          dateStyle: "medium",
        }).format(date),
        11,
        M,
        "middle",
      );
    out += text(200, 253, zone, 8.5, M, "middle", 'letter-spacing="1.3"');
    return out;
  }
  out += circle(
    200,
    151,
    77,
    simple(o) ? "none" : `url(#${svgId(o, "instrument")}-face)`,
    G,
    1,
  );
  for (let k = 1; k <= 12; k++) {
    const p = point(200, 151, 66, k * 30);
    out +=
      o.labels === "all" || k % 3 === 0
        ? text(p.x, p.y + 4, k, 11, M, "middle")
        : circle(p.x, p.y, simple(o) ? 1 : 1.6, M, "none");
  }
  const hand = (bearing, length, width, color) =>
    `<g transform="translate(200 151) rotate(${bearing})" ${motion}><path d="M${-width} 9 L${-width} -${length - 8} Q0 -${length + 2} ${width} -${length - 8} L${width} 9Z" fill="${color}"/></g>`;
  out +=
    hand(((h % 12) + m / 60) * 30, 38, 3.5, T) +
    hand((m + seconds / 60) * 6, 58, 2.5, A) +
    circle(200, 151, 5, B, T, 1.5);
  if (o.secondHand)
    out += marker(200, 151, 61, seconds * 6, "tick", "var(--attention)", 0.4);
  if (o.sourceLabel)
    out += text(
      200,
      252,
      `${zone} · ${short(s?.sourceLabel ?? "Clock", 27)}`,
      9,
      M,
      "middle",
    );
  return out;
}

function gaugeValue(id, data, o) {
  const role = id === "tank" ? "level" : "value",
    source = sample(data, role),
    raw = numberValue(data, role),
    value = displayNumber(data, role, o);
  if (raw === null || value === null) return null;
  const range =
    id === "arc-gauge"
      ? [o.min, o.max]
      : (o.range ?? o.bounds ?? [o.scaleMin, o.scaleMax]);
  const [min, max] = range;
  if (!numeric(min) || !numeric(max) || max <= min) return null;
  const fraction = clamp((value - min) / (max - min), 0, 1),
    reverse =
      o.fillDirection === "reverse" || o.direction === "increasing-vacuum";
  return {
    role,
    source,
    value,
    min,
    max,
    raw,
    fraction,
    position: reverse ? 1 - fraction : fraction,
    f: reading(data, role, o),
    reverse,
  };
}

function gaugeZones(o, g, geometry) {
  let out = "";
  for (const z of o.zones ?? []) {
    const low = clamp((z.min - g.min) / (g.max - g.min), 0, 1),
      high = clamp((z.max - g.min) / (g.max - g.min), 0, 1),
      a = g.reverse ? 1 - high : low,
      b = g.reverse ? 1 - low : high;
    if (geometry.arc)
      out += path(
        arc(
          geometry.cx,
          geometry.cy,
          geometry.r,
          geometry.start + a * geometry.sweep,
          geometry.start + b * geometry.sweep,
        ),
        tone(z.tone),
        geometry.width,
        part("zones", 'opacity=".45"'),
      );
    else if (geometry.vertical)
      out += rect(
        geometry.x,
        geometry.y + geometry.h * (1 - b),
        geometry.w,
        geometry.h * (b - a),
        tone(z.tone),
        2,
        part("zones", 'opacity=".24"'),
      );
    else
      out += rect(
        geometry.x + a * geometry.w,
        geometry.y,
        (b - a) * geometry.w,
        geometry.h,
        tone(z.tone),
        3,
        part("zones", 'opacity=".28"'),
      );
  }
  return out;
}

/**
 * A gauge with no reading on a valid scale (TOM-665): its scale alone, the
 * value a muted "—", nothing placed on the scale.
 */
function emptyGauge(id, data, o) {
  const role = id === "tank" ? "level" : "value";
  if (numberValue(data, role) !== null) return null;
  const [min, max] =
    id === "arc-gauge"
      ? [o.min, o.max]
      : (o.range ?? o.bounds ?? [o.scaleMin, o.scaleMax]);
  if (!numeric(min) || !numeric(max) || max <= min) return null;
  const reverse =
    o.fillDirection === "reverse" || o.direction === "increasing-vacuum";
  return {
    role,
    source: sample(data, role),
    value: null,
    min,
    max,
    raw: null,
    fraction: 0,
    position: reverse ? 1 : 0,
    f: reading(data, role, o),
    reverse,
    empty: true,
  };
}

function arcGauge(id, data, o) {
  const g = gaugeValue(id, data, o) ?? emptyGauge(id, data, o);
  if (!g) return missing("Numeric source or scale unavailable");
  const quiet = simple(o),
    sweep = o.sweep === "half" ? 180 : o.sweep === "full" ? 330 : 270,
    start = -sweep / 2,
    cy = o.sweep === "half" ? 200 : 153,
    cx = 200,
    r = o.sweep === "half" ? 106 : 78;
  const bearing = start + g.position * sweep;
  let out = defs(o);
  if (!quiet && o.sweep !== "half")
    out += circle(
      cx,
      cy,
      r - 14,
      `url(#${svgId(o, "instrument")}-face)`,
      G,
      0.65,
      part("face"),
    );
  out += path(
    arc(cx, cy, r, start, start + sweep),
    G,
    quiet ? 6 : 13,
    part("track"),
  );
  out += gaugeZones(o, g, {
    arc: true,
    cx,
    cy,
    r,
    start,
    sweep,
    width: quiet ? 6 : 13,
  });
  if (!g.empty)
    out += path(
      arc(cx, cy, r, start, start + Math.max(0.001, g.position * sweep)),
      A,
      id === "needle-gauge" ? (quiet ? 2 : 4) : quiet ? 6 : 10,
      part("fill", `opacity="${id === "needle-gauge" ? 0.38 : 0.9}" ${motion}`),
    );
  if (g.empty) {
    // No needle or pointer: nothing on the scale reads as a value.
  } else if (id === "needle-gauge") {
    out +=
      ribbon(cx, cy, r - 22, r + 5, bearing, quiet ? 7 : 13, A, 0.16) +
      marker(cx, cy, r, bearing, o.pointerStyle, A, quiet ? 1 : 1.15, "needle");
  } else {
    const q = point(cx, cy, r, bearing);
    out += circle(q.x, q.y, quiet ? 4 : 5, T, A, 2.5, part("pointer", motion));
  }
  const ticks = typeof o.ticks === "number" ? o.ticks : 5;
  for (let k = 0; k <= ticks; k++) {
    const a = start + (k / ticks) * sweep,
      p = point(cx, cy, r + 10, a),
      q = point(cx, cy, r + (quiet ? 14 : 17), a);
    if (!quiet || k === 0 || k === ticks || k === Math.floor(ticks / 2))
      out += line(p.x, p.y, q.x, q.y, M, 1, part("ticks"));
    if (o.tickLabels !== false && (!quiet || k === 0 || k === ticks)) {
      const label = point(cx, cy, r + 28, a);
      out += text(
        label.x,
        label.y + 3,
        (g.min + (k / ticks) * (g.max - g.min)).toFixed(0),
        8,
        M,
        "middle",
        part("tick-labels"),
      );
    }
  }
  const visualMarker = id === "vacuum" ? o.serviceMarker : o.referenceMarker;
  if (numeric(visualMarker) && visualMarker !== 0) {
    let f = clamp((visualMarker - g.min) / (g.max - g.min), 0, 1);
    if (g.reverse) f = 1 - f;
    out += marker(
      cx,
      cy,
      r + 5,
      start + f * sweep,
      "tick",
      "var(--attention)",
      0.55,
      "reference",
    );
  }
  if (o.showValue !== false) {
    const below = id === "needle-gauge" && o.valuePosition === "below",
      y = below ? 236 : cy + 10,
      size = below ? 33 : quiet ? 47 : 40,
      // Inside the arc a long value steps down to fit its inner width (the
      // face's ring, r − 14), as valueBlock fits its own (TOM-665).
      room = below || o.sweep === "half" ? Infinity : 2 * (r - 14) - 12;
    out +=
      text(
        cx,
        y,
        g.f.value,
        Math.round(
          Math.min(size, room / Math.max(1, String(g.f.value).length * 0.6)) *
            100,
        ) / 100,
        g.empty ? M : T,
        "middle",
        part("value", bold),
      ) + text(cx, y + 20, g.f.unit, 10, M, "middle", part("unit"));
  }
  if (id === "vacuum" && o.referenceLabel)
    out += text(
      200,
      254,
      short(g.source?.reference ?? "Pressure reference unavailable", 43),
      9,
      M,
      "middle",
      part("caption"),
    );
  return out;
}

function linearGauge(id, data, o) {
  const g = gaugeValue(id, data, o);
  if (!g) return missing("Numeric source or scale unavailable");
  const vertical = id === "vertical-meter" || o.orientation === "vertical",
    quiet = simple(o),
    labels = o.labels !== "none",
    display =
      o.labels === "percent" || o.showPercent
        ? `${Math.round(g.fraction * 100)}%`
        : g.f.value;
  let out = defs(o);
  if (vertical) {
    const x = quiet ? 64 : 60,
      y = 68,
      w = quiet ? 16 : 42,
      h = 164,
      fill = h * g.fraction,
      fillY = g.reverse ? y : y + h - fill;
    if (o.track !== "none") out += rect(x, y, w, h, G, w / 2, part("track"));
    out += gaugeZones(o, g, { vertical: true, x, y, w, h });
    out += rect(x, fillY, w, fill, A, w / 2, part("fill", motion));
    if (!quiet && fill > 0)
      out += rect(
        x + 4,
        fillY + 4,
        w - 8,
        Math.max(0, fill - 8),
        `url(#${svgId(o, "instrument")}-fill)`,
        12,
        part("fill"),
      );
    if (!quiet)
      for (let k = 0; k <= 8; k++)
        out += line(
          x + w + 8,
          y + (k * h) / 8,
          x + w + (k % 2 ? 12 : 17),
          y + (k * h) / 8,
          G,
          1,
          part("ticks"),
        );
    if (labels)
      out +=
        text(164, 154, display, 56, T, "start", part("value", bold)) +
        text(166, 180, g.f.unit, 12, M, "start", part("unit"));
    for (const threshold of o.thresholds ?? []) {
      const fy =
        y + h * (1 - clamp((threshold.value - g.min) / (g.max - g.min), 0, 1));
      out +=
        line(
          x - 4,
          fy,
          x + w + 6,
          fy,
          tone(threshold.tone),
          2,
          part("reference"),
        ) +
        text(
          x + w + 23,
          fy + 3,
          short(threshold.label || threshold.value, 24),
          8,
          M,
          "start",
          part("caption"),
        );
    }
    if (!quiet)
      out +=
        text(x, 255, g.min, 8, M, "start", part("low")) +
        text(x, 57, g.max, 8, M, "start", part("high"));
    if (id === "vertical-meter")
      out += text(
        164,
        219,
        o.direction === "highGood"
          ? "HIGHER IS PREFERRED"
          : o.direction === "lowGood"
            ? "LOWER IS PREFERRED"
            : "MEASURED LEVEL",
        8,
        M,
        "start",
        part("caption", 'letter-spacing=".9"'),
      );
  } else {
    const x = 32,
      y = o.valuePosition === "above" ? 173 : 176,
      w = 336,
      h = id === "borderless-bar" ? (quiet ? 5 : 11) : quiet ? 8 : 24;
    if (labels)
      out +=
        text(
          32,
          139,
          display,
          quiet ? 62 : 56,
          T,
          "start",
          part("value", bold),
        ) + text(34, 160, g.f.unit, 11, M, "start", part("unit"));
    if (o.track !== "none") out += rect(x, y, w, h, G, h / 2, part("track"));
    out += gaugeZones(o, g, { x, y, w, h });
    const fill = w * g.fraction,
      fillX = o.fillDirection === "reverse" ? x + w - fill : x;
    out += rect(
      fillX,
      y,
      fill,
      h,
      quiet ? A : `url(#${svgId(o, "instrument")}-track)`,
      h / 2,
      part("fill", motion),
    );
    if (id !== "borderless-bar")
      out += circle(
        o.fillDirection === "reverse" ? fillX : x + fill,
        y + h / 2,
        quiet ? 4 : 6,
        T,
        A,
        quiet ? 2 : 3,
        part("pointer", motion),
      );
    if (o.endpoints !== false && id !== "borderless-bar")
      out +=
        text(x, y + h + 24, g.min, 9, M, "start", part("low")) +
        text(x + w, y + h + 24, g.max, 9, M, "end", part("high"));
    if (numeric(o.targetMarker) && o.targetMarker !== 0) {
      const f = clamp((o.targetMarker - g.min) / (g.max - g.min), 0, 1),
        tx = x + (o.fillDirection === "reverse" ? 1 - f : f) * w;
      out +=
        line(
          tx,
          y - 6,
          tx,
          y + h + 7,
          "var(--attention)",
          2,
          part("reference"),
        ) +
        (!quiet
          ? text(
              tx,
              y + h + 41,
              `Target ${o.targetMarker}`,
              8,
              M,
              "middle",
              part("caption"),
            )
          : "");
    }
  }
  return out;
}

function tankFace(data, o) {
  const g = gaugeValue("tank", data, o);
  if (!g) return missing("Tank level or scale unavailable");
  const quiet = simple(o),
    cylinder = o.shape === "cylinder",
    x = quiet ? 45 : 40,
    y = 78,
    w = quiet ? 105 : 112,
    h = 155,
    clip = svgId(o, "tank"),
    fill = h * g.fraction,
    yFill = y + h - fill;
  let out =
    defs(o) +
    `<defs><clipPath id="${clip}">${rect(x, y, w, h, "white", cylinder ? 26 : 15)}</clipPath></defs>`;
  out += rect(
    x,
    y,
    w,
    h,
    quiet ? "none" : S,
    cylinder ? 26 : 15,
    part("track", `stroke="${G}" stroke-width="1.5"`),
  );
  out += `<g clip-path="url(#${clip})">${rect(x, yFill, w, fill, quiet ? A : `url(#${svgId(o, "instrument")}-fill)`, 0, part("fill", motion))}`;
  if (fill > 0)
    out += path(
      `M${x} ${yFill + 2} Q${x + w * 0.25} ${yFill - 3} ${x + w * 0.5} ${yFill + 2} T${x + w} ${yFill + 2}`,
      A,
      quiet ? 1.5 : 2.5,
      part("fill", motion),
    );
  out += "</g>";
  if (cylinder && !quiet)
    out += `<ellipse cx="${x + w / 2}" cy="${y + 14}" rx="${w / 2 - 5}" ry="10" fill="none" stroke="${G}" ${part("track")}/>`;
  if (!quiet)
    for (const p of [0.25, 0.5, 0.75])
      out += line(
        x + 7,
        y + h * p,
        x + 19,
        y + h * p,
        M,
        1,
        part("ticks", 'opacity=".45"'),
      );
  if (numeric(o.lowMarker) && o.lowMarker !== 0) {
    const capacity = numberValue(data, "capacity"),
      fraction =
        capacity !== null && capacity > 0
          ? o.lowMarker / capacity
          : (o.lowMarker - g.min) / (g.max - g.min),
      my = y + h * (1 - clamp(fraction, 0, 1));
    out +=
      line(
        x - 5,
        my,
        x + w + 5,
        my,
        "var(--attention)",
        1.5,
        part("reference"),
      ) +
      text(
        x + w + 8,
        my + 3,
        "LOW",
        7,
        "var(--attention)",
        "start",
        part("low"),
      );
  }
  if (o.display !== "quantity")
    out += text(
      259,
      o.display === "both" ? 137 : 159,
      `${Math.round(g.fraction * 100)}%`,
      quiet ? 50 : 46,
      T,
      "middle",
      part("value", bold),
    );
  if (o.display !== "percent") {
    const quantity = reading(data, "quantity", o);
    out +=
      text(
        259,
        o.display === "both" ? 184 : 158,
        quantity.value,
        o.display === "both" ? 31 : 47,
        T,
        "middle",
        part(o.display === "both" ? "secondary" : "value", bold),
      ) +
      text(
        259,
        o.display === "both" ? 205 : 182,
        quantity.unit,
        11,
        M,
        "middle",
        part("unit"),
      );
  }
  if (o.fluidLabel)
    out += text(
      259,
      234,
      short(data.service?.fluid ?? g.source?.quantity ?? "Fluid", 24),
      10,
      M,
      "middle",
      part("caption"),
    );
  if (o.shape === "asset")
    out += text(
      200,
      58,
      `Asset · ${short(o.asset || "not selected", 36)}`,
      8.5,
      M,
      "middle",
      part("caption"),
    );
  return out;
}

export function renderInstrument(id, data, o, context = {}) {
  void context;
  if (id === "compass" || id === "head-up") return compassFace(id, data, o);
  if (id === "wind-rose") return windRose(data, o);
  if (id === "close-wind") return closeWind(data, o);
  if (id === "wind-angle") return windAngle(data, o);
  if (["rudder", "turn-rate", "heel"].includes(id))
    return balanceFace(id, data, o);
  if (id === "attitude") return attitudeFace(data, o);
  if (id === "set-drift") return setDrift(data, o);
  if (id === "number" || id === "trend-number") return numberFace(id, data, o);
  if (id === "state") return stateFace(data, o);
  if (id === "text-value") return textFace(data, o);
  if (id === "multi-text" || id === "status-strip")
    return collectionFace(id, data, o);
  if (id === "counter") return counterFace(data, o);
  if (id === "timer") return timerFace(data, o);
  if (id === "analog-clock" || id === "digital-clock")
    return clockFace(id, data, o);
  if (["arc-gauge", "needle-gauge", "vacuum"].includes(id))
    return arcGauge(id, data, o);
  if (id === "tank") return tankFace(data, o);
  if (["vertical-meter", "horizontal-meter", "borderless-bar"].includes(id))
    return linearGauge(id, data, o);
  throw new Error(`Unknown instrument renderer: ${String(id)}`);
}
